import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { ApiClientAccountOut, ApiExpectedStrategy, ClientAccountAdditionNewIn, ClientBankAccountOut, ClientRequestInput, ConsultantImageState, ConsultantState, NewPortfolioInput, PortfolioDepositOut, PortfolioFeeConfigsOut, PortfolioOut } from '../rest-api/rest-api';
import { RestApiService } from '../rest-api/rest-api.service';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';
import { AcceptEfektikaDraftModalChaning, CancelRegularWithdrawal, DraftAcceptOrRefuseModalInput, EfektikaDraftAcceptModalInput, EfektikaModalLoadingInput, MoneyDepositInput, RegularWithdrawalDraft, RegularWithdrawalMessageModalInput, WithdrawModalInput } from './modal';
import { filter, map, take } from 'rxjs/operators';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { Router } from '@angular/router';
import { VideoDetail } from 'src/app/pages/user/videos/videos';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import * as saveAs from 'file-saver';
import { Browser } from '@capacitor/browser';
import { AcceptDraftResponse, ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { SignalsService } from '../signals.service';
import { instrumentsNotInAllowedList, isDraftExpiredError, notAllowedStrategies } from 'src/app/shared/functions-angular';
import Decimal from 'decimal.js';
import { ClientConsultService } from '../client-consult.service';
import { ConfigService } from '../json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { AcceptEfektikaModalSource, AcceptEfektikaNextAction, EfektikaClientService } from '../efektika-client.service';
import { parseToError } from 'src/app/shared/functions-angular';
import { NativeCheckService } from '../native-check.service';

export type ModalDisplay = 'close' | 'open';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private display: BehaviorSubject<ModalDisplay> = new BehaviorSubject<ModalDisplay>('close');

  public preloader = false;
  public dipPreloader = false;
  public disableClose = false;
  public apiError = false;
  public currency: string | null = null;


  // used after close for route redirect or page reload
  private afterCloseRedirect: string | null = null;

  // modal type/content data - only one can be not-null!
  public consultant: {value: ConsultantState, image: ConsultantImageState} | null = null;
  public moneyDeposit: MoneyDepositInput | null = null;
  public rebalancingControl: AbstractControl | null = null;
  public dipControl: AbstractControl | null = null;
  public executionSwitchAddition: true | null = null;
  public profileFeesConfirm: true | null = null;
  public portfoliosMigrateAddition: true | null = null;
  public clientAddition: ClientAccountAdditionNewIn | null = null;
  public dipOpenSource: 'new-strategy' | 'new-portfolio' | null = null;
  public withdraw: WithdrawModalInput | null = null;
  public withdrawConfirmTemp: WithdrawModalInput | null = null;
  public videoPreview: VideoDetail | null = null;
  public unsignedAdditionId: number | null = null;
  public preferedContractBankAccount: string | null = null;
  public sucessfullySigned: true | null = null;
  public additionalSignErrored: true | null = null;
  public etfLanguageConfirm: true | null = null;
  public investmentQuestionaireExpired: true | null = null;
  public rebalancingSwitchoffConfirm: true | null = null;
  public feeChangeConfirm: true | null = null;
  public portfolioToDelete: ExpectedOwnedStrategy | null = null;
  public feeConfigs: PortfolioFeeConfigsOut | null = null;
  public investmentConfirm: ApiExpectedStrategy | NewPortfolioInput | null = null;
  public initSignIn: true | null = null;
  public draftAcceptRefuseConfirm: DraftAcceptOrRefuseModalInput | null = null;
  public efektikaDraftAcceptConfirm: EfektikaDraftAcceptModalInput | null = null;
  public efektikaDraftAcceptDipConfirm: EfektikaModalLoadingInput | null = null;
  public acceptEfektikaDraftModalChaning: AcceptEfektikaDraftModalChaning | null = null;
  public efektikaDraftAcceptRegularExceedConfirm: EfektikaModalLoadingInput | null = null;
  public newPortfolioVatConfirm: boolean | null = null;
  public draftPortfolioVatConfirm: { draftId: number, preloader: boolean } | null = null;
  public cancelRegularWithdrawal: CancelRegularWithdrawal | null = null;
  public newRegularWithdrawal: RegularWithdrawalDraft | null = null;
  public draftWithdrawalComment: RegularWithdrawalMessageModalInput | null = null;
  public transactionsExportForm: boolean | null = null;

  public get containsNotNullData(): boolean {
    return !!this.withdraw || !!this.rebalancingControl || !!this.moneyDeposit || !!this.dipControl || !!this.unsignedAdditionId || !!this.sucessfullySigned || !!this.preferedContractBankAccount || !!this.additionalSignErrored || !! this.withdrawConfirmTemp || !!this.etfLanguageConfirm || !!this.investmentQuestionaireExpired || !!this.portfolioToDelete || !!this.investmentConfirm || !!this.initSignIn || !!this.rebalancingSwitchoffConfirm || !!this.feeChangeConfirm || !!this.draftAcceptRefuseConfirm || !!this.executionSwitchAddition || !!this.portfoliosMigrateAddition || !!this.clientAddition || !!this.feeConfigs || !!this.efektikaDraftAcceptConfirm || !!this.efektikaDraftAcceptDipConfirm || !!this.efektikaDraftAcceptRegularExceedConfirm || !!this.newPortfolioVatConfirm || !!this.profileFeesConfirm || !!this.cancelRegularWithdrawal || !!this.newRegularWithdrawal || !!this.draftWithdrawalComment;
  }

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private router: Router,
    private signalsService: SignalsService,
    private clientConsultService: ClientConsultService,
    private configService: ConfigService,
    private efektikaClientService: EfektikaClientService,
    private nativeCheckService: NativeCheckService,
  ) { }

  public watch(): Observable<string> {
    return this.display.asObservable();
  }

  public openMoneyDepositModal(portfolioId: number): void {
    this.preloader = true;
    this.display.next('open');

    const neccessaryInitReqests = [
      this.restApiService.getMoneyDeposit({ portfolioId }),
      this.restApiService.getInvestmentDetail({ portfolioId }),
    ];

    forkJoin(neccessaryInitReqests).pipe(
      take(1),
      map(
        ([ deposit, investmenDetail ]) =>
        ({ deposit, investmenDetail } as { deposit: PortfolioDepositOut, investmenDetail: PortfolioOut })
      ),
    ).subscribe({
        error: this.handleApiError.bind(this),
        next: this.handleMoneyDepositData.bind(this),
    });
  }

  public openMoneyDepositModalDirect(data: { deposit: PortfolioDepositOut, investmenDetail: PortfolioOut }): void {
    this.preloader = true;
    this.display.next('open');
    this.handleMoneyDepositData(data);
  }

  private handleMoneyDepositData(data: { deposit: PortfolioDepositOut, investmenDetail: PortfolioOut }): void {
    this.moneyDeposit = {
      name: data.investmenDetail.name,
      accountNumber: data.deposit.accountNumber,
      variableSymbol: data.deposit.variableSymbol,
      strategy: data.investmenDetail.portfolioType === 'STRATEGY',
      portfolio: data.investmenDetail.portfolioType === 'PORTFOLIO',
      regularDeposit: data.deposit.regularDeposit,
      bankCode: data.deposit.bankCode,
      currency: data.deposit.currency,
      portfolioId: data.investmenDetail.portfolioId,
      dip: data.investmenDetail.dip,
      entryFeeAlreadyPaid: data.deposit.entryFeeInfo?.entryFeeAlreadyPaid === 0 ? 0 : (data.deposit.entryFeeInfo?.entryFeeAlreadyPaid ?? null),
      totalEntryFee: data.deposit.entryFeeInfo?.totalEntryFee ?? NaN,
      unpaidAmountWithVat: data.deposit.entryFeeInfo?.unpaidAmountWithVat ?? NaN,
      vatAlreadyPaid: data.deposit.entryFeeInfo?.vatAlreadyPaid ?? NaN,
      entryFeeAlreadyPaidWithVat: data.deposit.entryFeeInfo?.entryFeeAlreadyPaidWithVat ?? NaN,
      feeAlreadyPaid: Decimal.sub(data.deposit.entryFeeInfo?.entryFeeAlreadyPaidWithVat ?? 0, data.deposit.entryFeeInfo?.vatAlreadyPaid ?? 0).toNumber(),
    }
    this.preloader = false;
  }

  private handleApiError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openConsultModal(): void {
    this.withdrawConfirmTemp = null;
    this.display.next('open');
    this.preloader = true;

    this.clientConsultService.preloader$.pipe(
      filter(value => !value),
      take(1),
    ).subscribe(() => {
      this.consultant = {
        value: this.clientConsultService.clientConsult,
        image: this.clientConsultService.clientConsultImage,
      }
      this.preloader = false;
    });
  }

  public openRebalancingModal(control: AbstractControl): void {
    this.rebalancingControl = control;
    this.display.next('open');
  }

  public confirmRebalancing(): void {
    this.rebalancingControl!.patchValue(true);
    this.close();
  }

  public openInvestmentDelete(portfolioToDelete: ExpectedOwnedStrategy): void {
    this.portfolioToDelete = portfolioToDelete;
    this.display.next('open');
  }

  public openPortfolioFeesModal(params: ClientRequestInput, currency: string): void {
    this.currency = currency;
    this.restApiService.getPortfolioFees(params).subscribe({
      error: (error) => this.handlePortfolioFeesError(error),
      next: (data) => this.handlePortfolioFees(data),
    });
    this.preloader = true;
    this.display.next('open');
  }

  private handlePortfolioFees(data: PortfolioFeeConfigsOut): void {
    this.feeConfigs = data;
    this.apiError = false;
    this.preloader = false;
  }

  private handlePortfolioFeesError(error: unknown): void {
    this.preloader = false;
    this.apiError = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public confirmInvestmentDelete(): void {
    this.preloader = true;
    if(!this.portfolioToDelete) {
      throw new Error('Not defined which investment should be deleted!');
    }
    this.restApiService.deleteInvestment(this.portfolioToDelete.portfolioId).subscribe({
      error: this.investmentDeleteError.bind(this),
      next: this.handleInvestmentDelete.bind(this),
    });
  }

  private handleInvestmentDelete(): void {
    this.preloader = false;
    this.toastService.addNotification({
      title: 'toastNotifications.headers.send',
      message: 'toastNotifications.messages.investmentDeleted',
      type: 'success',
      component: ToastNotificationComponent
    });

    this.signalsService.updateSummaryOwnedInvestments();
    this.signalsService.investmentDetailDeleted();
    this.close();
  }

  private investmentDeleteError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openDipModal(control: AbstractControl, type: 'new-strategy' | 'new-portfolio'): void {
    this.dipControl = control;
    this.dipOpenSource = type;

    this.display.next('open');
  }

  public openEfektikaDipModal(): void {
    // reset possible previous modal contents
    this.efektikaDraftAcceptConfirm = null;

    this.efektikaDraftAcceptDipConfirm = { loading: false };
    this.dipOpenSource = 'new-strategy';
    this.display.next('open');
  }

  public openEfektikaRegularExceededModal(): void {
    // reset possible previous modal contents
    this.dipControl = null;
    this.dipOpenSource = null;
    this.efektikaDraftAcceptDipConfirm = null;
    this.efektikaDraftAcceptConfirm = null;

    this.efektikaDraftAcceptRegularExceedConfirm = { loading: false };
    this.display.next('open');
  }

  public confirmDip(): void {
    this.dipControl!.patchValue(true);
    this.close();
  }

  public confirmDipEfektika(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION, queueState);
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.DIP);
  }

  public confirmRegularExceededEfektika(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.REGULAR_EXCEEDED);
  }

  private confirmEfektikaDraft(): void {
    // controlls that only one action will trigger and others are ignored
    const queueState = { open: true };
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_DIP_CONFIRMATION, queueState);
    this.triggerEfektikaModalActionIfPresent(AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION, queueState);
    this.triggerEfektikaCreateActionIfPresent(queueState, AcceptEfektikaModalSource.EFEKTIKA);
  }

  public openExecutionSwitchConfirm(): void {
    this.executionSwitchAddition = true;
    this.display.next('open');
  }

  public openProfileFeesTransferConfirmModal(): void {
    this.profileFeesConfirm = true;
    this.display.next('open');
  }

  public confirmProfileFeesTransferConfirmModal(): void {
    this.preloader = true;
    this.restApiService.createClientAddition(ClientAccountAdditionNewIn.OPF_TRANSFER).subscribe({
      error: this.feesTransferRequestError.bind(this),
      next: this.feesTransferRequestSent.bind(this),
    });
  }

  private feesTransferRequestSent(): void {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.send',
      message: 'toastNotifications.messages.opfTransferSuccess',
      type: 'success',
      component: ToastNotificationComponent
    });

    this.afterCloseRedirect = '/user';
    this.close();
  }

  private feesTransferRequestError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.opfTransferError',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  public openPortfoliosMigrateConfirm(): void {
    this.portfoliosMigrateAddition = true;
    this.display.next('open');
  }

  public requestSignExecutionSwitch(): void {
    this.preloader = true;
    this.restApiService.createClientAddition(ClientAccountAdditionNewIn.INVEST_QUEST_INSTANT_CATEGORY).subscribe({
      error: this.signRequestError.bind(this),
      next: this.signRequestSent.bind(this),
    });
  }

  public requestSignPortfoliosMigrate(): void {
    this.preloader = true;
    this.restApiService.createClientAddition(ClientAccountAdditionNewIn.PM_TO_EO_CONVERSION).subscribe({
      error: this.signRequestError.bind(this),
      next: this.signRequestSent.bind(this),
    });
  }

  private signRequestSent(): void {
    if(this.executionSwitchAddition) {
      this.clientAddition = ClientAccountAdditionNewIn.PM_TO_EO_CONVERSION;
    } else if (this.portfoliosMigrateAddition) {
      this.clientAddition = ClientAccountAdditionNewIn.INVEST_QUEST_INSTANT_CATEGORY;
    }

    // after confirm user can close multiple ways, ensure page reload anyway
    this.afterCloseRedirect = '/user';

    this.executionSwitchAddition = null;
    this.portfoliosMigrateAddition = null;
    this.preloader = false;
  }

  private signRequestError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.additionalSignError',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  public requestConsultation(): void {
    this.preloader = true;

    this.restApiService.requestConsultMeeting().subscribe({
      error: this.handleMeetingError.bind(this),
      next: this.handleMeeting.bind(this),
    });
  }

  private handleMeeting(): void {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.send',
      message: 'toastNotifications.messages.meetingSend',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.close();
  }

  private handleMeetingError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openWithdrawModal(withdrawData: WithdrawModalInput): void {
    this.withdraw = withdrawData;
    this.display.next('open');
  }

  public openWithdrawFromConfirmModal(): void {
    const tempData = this.withdrawConfirmTemp;
    this.close();
    this.withdraw = tempData;
    this.display.next('open');
  }

  public openWithdrawConsultOfferModal(withdrawData: WithdrawModalInput): void {
    this.withdrawConfirmTemp = withdrawData;
    this.display.next('open');
  }

  public openVideoModal(video: VideoDetail): void {
    this.videoPreview = video;
    if(this.nativeCheckService.isNativePlatform()) {
      ScreenOrientation.unlock();
    }
    this.display.next('open');
  }

  public confirmWithdraw(): void {
    this.preloader = true;

    if(!this.withdraw) {
      throw new Error('Withdraw data not found!');
    }

    if(this.withdraw.modalType === 'OVER-LIMIT' || this.withdraw.modalType === 'ALL') {
      this.restApiService.withdrawAll(
        this.withdraw.investmentId,
        this.withdraw.clientBankAccountId,
        ).subscribe({
          error: this.handleWithdrawError.bind(this),
          next: this.handleWithdraw.bind(this),
        });
      } else {
        this.restApiService.withdrawInvestment({
          portfolioId: parseInt(this.withdraw.investmentId),
          clientBankAccountId: this.withdraw.clientBankAccountId,
          amount: this.withdraw.amount,
        }).subscribe({
          error: this.handleWithdrawError.bind(this),
          next: this.handleWithdraw.bind(this),
        });
      }
  }

  private handleWithdrawError(error: unknown): void {
    this.preloader = true;
    const parsedError = parseToError(error);

    if(
      parsedError &&
      parsedError.error &&
      this.withdraw &&
      parsedError.status === 400
    ) {
        const tresholdErrorMessage = 'Sum of unprocessed withdrawals are bigger the threshold for liquidation.';
        const withdrawsErrorMessage = 'Portfolio has some unprocessed withdrawals.';
        const liquidationErrorMessage = 'Portfolio is in process of liquidation.';
        const message = parsedError.error.message;

        if(message.includes(tresholdErrorMessage)) {
          const tempWithdraw = this.withdraw;
          tempWithdraw.modalType = 'OVER-LIMIT';
          this.close();
          this.openWithdrawModal(tempWithdraw);
        } else if(message.includes(liquidationErrorMessage)) {
          this.preloader = false;
          this.toastService.addNotification({
            title: 'toastNotifications.headers.error',
            message: 'toastNotifications.messages.unprocessedWithdrawalsError',
            type: 'warning',
            component: ToastNotificationComponent
          });
        } else if(message.includes(withdrawsErrorMessage)) {
          this.preloader = false;
          this.toastService.addNotification({
            title: 'toastNotifications.headers.error',
            message: 'toastNotifications.messages.unprocessedWithdrawalsError',
            type: 'warning',
            component: ToastNotificationComponent
          });
        } else {
          this.finalizeWithdrawError(error);
        }
    } else {
      this.finalizeWithdrawError(error);
    }
  }

  private finalizeWithdrawError(error: unknown):void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleWithdraw(): void {
    this.preloader = false;
    this.toastService.addNotification({
      title: 'toastNotifications.headers.send',
      message: 'toastNotifications.messages.withdrawRequestAccepted',
      type: 'success',
      component: ToastNotificationComponent
    });

    if(this.withdraw?.dip) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.dipEmailConfirmation',
        type: 'success',
        disableAutohide: true,
        component: ToastNotificationComponent
      });
    }
    this.router.navigate(['/money/withdraw']);
    this.close();
  }

  public close(forceRefreshRoute?: string): void {
    this.currency = null;
    this.apiError = false;
    this.preloader = false;

    this.rebalancingControl = null;
    this.dipControl = null;
    this.consultant = null;
    this.moneyDeposit = null;
    this.withdraw = null;
    this.withdrawConfirmTemp = null;
    this.videoPreview = null;
    this.disableClose = false;
    this.unsignedAdditionId = null;
    this.preferedContractBankAccount = null;
    this.sucessfullySigned = null;
    this.additionalSignErrored = null;
    this.etfLanguageConfirm = null;
    this.investmentQuestionaireExpired = null;
    this.portfolioToDelete = null;
    this.feeConfigs = null;
    this.investmentConfirm = null;
    this.initSignIn = null;
    this.rebalancingSwitchoffConfirm = null;
    this.feeChangeConfirm = null;
    this.draftAcceptRefuseConfirm = null;
    this.clientAddition = null;
    this.executionSwitchAddition = null;
    this.portfoliosMigrateAddition = null;
    this.efektikaDraftAcceptConfirm = null;
    this.efektikaDraftAcceptDipConfirm = null;
    this.efektikaDraftAcceptRegularExceedConfirm = null;
    this.newPortfolioVatConfirm = null;
    this.draftPortfolioVatConfirm = null;
    this.profileFeesConfirm = null;
    this.cancelRegularWithdrawal = null;
    this.newRegularWithdrawal = null;
    this.draftWithdrawalComment = null;
    this.acceptEfektikaDraftModalChaning = null;
    this.transactionsExportForm = null;

    if(this.nativeCheckService.isNativePlatform()) {
      ScreenOrientation.lock({orientation: 'portrait'});
    }
    this.display.next('close');
    this.signalsService.modalClose();

    const afterCloseRedirectRoute = this.afterCloseRedirect ?? forceRefreshRoute;
    if(afterCloseRedirectRoute) {
      this.afterCloseRedirect = null;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([afterCloseRedirectRoute]);
      });
    }
  }

  public generateDipPdf(): void {
    if(!this.moneyDeposit) {
      console.error('Undefined deposit object.')
      return;
    }

    this.dipPreloader = true;
    this.restApiService.employerPaymentPdf({
      portfolioId: this.moneyDeposit.portfolioId,
    }).subscribe({
      error: this.handleDipPdfError.bind(this),
      next: this.downloadDipPdf.bind(this),
    });
  }

  private downloadDipPdf(data: {link: string}): void {
    this.dipPreloader = false;

    if(this.nativeCheckService.isNativePlatform()) {
      Browser.open({ url: data.link });
    } else {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', data.link, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        if (xhr.status === 200) {
          let fileName;

          try {
            fileName = xhr.getResponseHeader('content-disposition')?.split('\'\'')[1];
            if(!fileName) {
              throw new Error(`Failed to parse response when getting filename from ${xhr.getResponseHeader('content-disposition')}`);
            }
            fileName = decodeURI(fileName);
          } catch (error) {
            this.loggerService.error('Failed to parse filename from header. Using default name.', error)
          }

          const blob = new Blob([xhr.response], { type: 'application/pdf' });
          saveAs(blob, fileName ?? 'DIP_Platebni_Údaje.pdf');
        } else {
          this.toastService.addNotification({
            title: 'toastNotifications.headers.error',
            message: 'toastNotifications.messages.documentDipError',
            type: 'warning',
            component: ToastNotificationComponent
          });
        }
      };

      xhr.send();
    }
  }

  private handleDipPdfError(error: unknown): void {
    this.dipPreloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public openAdditionalSignModal(unsignedAdditionId: number, frameworkContractId: number): void {
    this.disableClose = true;
    this.preloader = true;
    this.unsignedAdditionId = unsignedAdditionId;

    this.restApiService.getContractsBankAccounts(frameworkContractId).subscribe({
      error: this.handleContractsBankAccountsError.bind(this),
      next: this.handleContractsBankAccountsData.bind(this),
    });

    this.recheckUserDataAfterDelay();
    this.display.next('open');
  }

  public openInvestmentQuestionaireExpired(): void {
    this.disableClose = true;
    this.preloader = false;
    this.investmentQuestionaireExpired = true;
    this.display.next('open');
  }

  public openRebalancingSwitchoffConfirm(): void {
    this.rebalancingSwitchoffConfirm = true;
    this.display.next('open');
  }

  public openFeeChangeConfirm(): void {
    this.feeChangeConfirm = true;
    this.display.next('open');
  }

  public confirmRebalancingSwitchoff(): void {
    this.signalsService.confirmRebalancingSwitch();
    this.close();
  }

  public confirmFeesChange(): void {
    this.signalsService.confirmFeesChange();
    this.close();
  }

  private handleContractsBankAccountsData(data: ClientBankAccountOut[]): void {
    if(data[0]) {
      this.preferedContractBankAccount = data[0].accountNumber + '/' + data[0].bankCode;
    } else {
      this.preferedContractBankAccount = '';
    }
    this.preloader = false;
  }

  private handleContractsBankAccountsError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.preloader = false;
    this.toastService.unknownApiError();
  }

  public postAdditionSignMail(): void {
    this.restApiService.generateAdditionSignMail(this.unsignedAdditionId!).subscribe({
      error: this.handleAdditionSignError.bind(this),
      next: this.handleAdditionSignData.bind(this),
    });
    this.close();

    this.preloader = true;
    this.display.next('open');
  }

  private handleAdditionSignError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.preloader = false;
    this.disableClose = true;
    this.additionalSignErrored = true;
    this.toastService.unknownApiError();
  }

  private handleAdditionSignData(): void {
    this.toastService.addNotification({
      title: 'toastNotifications.headers.send',
      message: 'toastNotifications.messages.additionalSignEmailConfirmation',
      type: 'success',
      component: ToastNotificationComponent
    });

    this.openSuccesfullySignedAdditionModal();
  }

  private openSuccesfullySignedAdditionModal(): void {
    this.disableClose = true;
    this.sucessfullySigned = true;
    this.preloader = false;
    this.display.next('open');
    this.recheckUserDataAfterDelay();
  }

  // periodic check if mail-signed so modal can be closed
  private recheckUserDataAfterDelay(): void {
    setTimeout(() => {
      this.restApiService.getUserDetail().subscribe({
        error: this.handleSignPeriodicCheckError.bind(this),
        next: this.handleSignPeriodicCheckData.bind(this),
      });
    }, 15000);
  }

  private handleSignPeriodicCheckError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.disableClose = true;
    this.preloader = false;
    this.toastService.unknownApiError();
  }

  private handleSignPeriodicCheckData(userData: ApiClientAccountOut): void {
    const unassignedContract = userData.validContracts.find(contract => contract.unsignedAdditions.find((addition) => addition.type === 'MN_415'));
    if(!unassignedContract) {
      this.close();
    } else {
      this.recheckUserDataAfterDelay();
    }
  }

  public openConfirmEtfUpdate(): void {
    this.preloader = false;
    this.etfLanguageConfirm = true;
    this.display.next('open');
  }

  public openDraftAcceptRefuseConfirm(portfolio: ExpectedOwnedStrategy, type: 'accept' | 'refuse', origin: 'summary' | 'detail'): void {
    this.preloader = false;
    this.draftAcceptRefuseConfirm = { portfolio, type, origin, loading: false };
    this.display.next('open');
  }

  public openEfektikaDraftAcceptConfirm(draftPortfolio: ExpectedOwnedStrategy): void {
    this.acceptEfektikaDraftModalChaning = null;
    this.acceptEfektikaDraftModalChaning = {
      actions: [AcceptEfektikaNextAction.CREATE_REQUEST],
      draftPortfolio,
    };

    this.preloader = false;
    // accepting order: EFEKTIKA MODAL, DIP MODAL, REGULAR LIMIT MODAL
    if (draftPortfolio.dip) {
      this.acceptEfektikaDraftModalChaning.actions.push(AcceptEfektikaNextAction.MODAL_DIP_CONFIRMATION);
    }
    if (draftPortfolio.regularDeposit >= this.configService.getConfig(EnvironmentKeys.limitForInvestmentCreateConfirm)) {
      this.acceptEfektikaDraftModalChaning.actions.push(AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION);
    }
    this.efektikaDraftAcceptConfirm = { draftPortfolio, loading: false };
    this.display.next('open');
  }

  public onAcceptOrRefuseDraft(modalInput: DraftAcceptOrRefuseModalInput | null): void {
    if (!modalInput || !modalInput.portfolio || !modalInput.portfolio.draftPortfolioId) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.notDraft',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    if (modalInput.type === 'accept') {
      if(modalInput.portfolio.portfolioType === 'PORTFOLIO') {
        this.openDraftPortfolioVatConfirm(modalInput.portfolio.draftPortfolioId);
      } else {
        this.acceptDraftPortfolio(modalInput.portfolio.draftPortfolioId);
      }
    }

    if (modalInput.type === 'refuse') {
      this.restApiService.refuseDraftPortfolio(modalInput.portfolio.draftPortfolioId).subscribe({
        next: () => this.handleDraftRefused(modalInput.origin),
        error: this.handleRefuseDraftError.bind(this),
      });
    }
  }

  public onAcceptEfektikaDraft(modalInput: EfektikaDraftAcceptModalInput | null): void {
    if (!modalInput || !modalInput.draftPortfolio || !modalInput.draftPortfolio.draftPortfolioId || !modalInput.draftPortfolio.efektika || !this.efektikaDraftAcceptConfirm || !this.acceptEfektikaDraftModalChaning) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.notEfektikaDraft',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    this.confirmEfektikaDraft();
  }

  private triggerEfektikaModalActionIfPresent(action: AcceptEfektikaNextAction, queueState: { open: boolean }): void {
    if (!this.acceptEfektikaDraftModalChaning || !queueState.open) return;
    const { actions } = this.acceptEfektikaDraftModalChaning;

    if (actions.includes(action)) {
      this.efektikaClientService.triggerAcceptingEfektikaNextAction(action);
      this.efektikaDraftAcceptConfirm = null;
      queueState.open = false;
    }
  }

  private triggerEfektikaCreateActionIfPresent(queueState: { open: boolean }, modalSource: AcceptEfektikaModalSource): void {
    if (!this.acceptEfektikaDraftModalChaning || !queueState.open) return;
    const { actions, draftPortfolio } = this.acceptEfektikaDraftModalChaning;
    if (
      actions.includes(AcceptEfektikaNextAction.CREATE_REQUEST) &&
      draftPortfolio
    ) {
      this.efektikaClientService.triggerAcceptingEfektikaNextAction(
        AcceptEfektikaNextAction.CREATE_REQUEST, this.acceptEfektikaDraftModalChaning.draftPortfolio.draftPortfolioId
      );
      if (modalSource === AcceptEfektikaModalSource.EFEKTIKA && this.efektikaDraftAcceptConfirm) {
        this.efektikaDraftAcceptConfirm.loading = true;
      }
      if (modalSource === AcceptEfektikaModalSource.DIP && this.efektikaDraftAcceptDipConfirm) {
        this.efektikaDraftAcceptDipConfirm.loading = true;
      }
      if (modalSource === AcceptEfektikaModalSource.REGULAR_EXCEEDED && this.efektikaDraftAcceptRegularExceedConfirm) {
        this.efektikaDraftAcceptRegularExceedConfirm.loading = true;
      }
    }
  }

  public handleDraftAccepted(res: AcceptDraftResponse): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.draftAccepted',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.close();
    this.router.navigate(['/investment-detail/' + res.portfolioId, { isDraft: 0}]);
  }

  private handleDraftRefused(origin: 'detail' | 'summary'): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.draftRefused',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.close();
    if (origin === 'detail') {
      this.router.navigate(['/summary']);
    } else {
      this.signalsService.updateSummaryOwnedInvestments();
    }
  }

  public openNewInvestmentConfirm(newInvestment: ApiExpectedStrategy | NewPortfolioInput): void {
    this.signalsService.confirmCreateNewPortfolioHighRegular(null);
    this.signalsService.confirmCreateNewStrategyHighRegular(null);
    this.investmentConfirm = newInvestment;
    this.display.next('open');
  }

  public openPortfolioVatConfirm(): void {
    this.signalsService.confirmCreateNewPortfolioVat(false);
    this.newPortfolioVatConfirm = true;
    this.display.next('open');
  }

  public openDraftPortfolioVatConfirm(draftId: number): void {
    this.draftAcceptRefuseConfirm = null;
    this.draftPortfolioVatConfirm = { draftId, preloader: false };
    this.display.next('open');
  }

  private acceptDraftPortfolio(inputDraftId?: number): void {
    if(this.draftPortfolioVatConfirm) {
      this.draftPortfolioVatConfirm.preloader = true;
    }
    const draftId = inputDraftId ?? this.draftPortfolioVatConfirm?.draftId;
    if(!draftId) {
      this.handleAcceptDraftError('Missing draft to accept ID!');
    } else {
      this.restApiService.acceptDraftPortfolio(draftId).subscribe({
        next: this.handleDraftAccepted.bind(this),
        error: this.handleAcceptDraftError.bind(this),
      });
    }
  }

  public handlePortfolioVatAccept(): void {
    if(this.draftPortfolioVatConfirm) {
      this.acceptDraftPortfolio();
    } else {
      this.signalsService.confirmCreateNewPortfolioVat(true);
      this.close();
    }
  }

  public displayDraftWithdrawalComment(input: RegularWithdrawalMessageModalInput): void {
    this.draftWithdrawalComment = input;
    this.display.next('open');
  }

  public confirmNewInvestmentHighRegular(): void {
    if(this.isNewInvestPortfolio) {

      this.signalsService.confirmCreateNewPortfolioHighRegular(this.investmentConfirm as NewPortfolioInput);
    } else {
      this.signalsService.confirmCreateNewStrategyHighRegular(this.investmentConfirm as ApiExpectedStrategy);
    }

    this.close();
  }

  public get isNewInvestPortfolio(): boolean {
    return (this.investmentConfirm as NewPortfolioInput)?.riskScore !== undefined;
  }

  public confirmEtfUpdate(): void {
    this.preloader = true;
    this.restApiService.canReadEnglish().subscribe({
      error: this.handleReadEnglishError.bind(this),
      next: this.handleReadEnglish.bind(this),
    });
  }

  private handleReadEnglishError(error: unknown): void {
    this.preloader = false;
    this.signalsService.updateEtf();

    this.toastService.unknownApiError();
    this.loggerService.error(typeof error === 'function' ? error() : error);
  }

  private handleRefuseDraftError(error: unknown): void {
    this.preloader = false;

    if (isDraftExpiredError(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.draftExpired',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.toastService.unknownApiError();
    }
    this.loggerService.error(error);
    this.close();
  }

  public handleAcceptDraftError(error: unknown): void {
    this.preloader = false;
    if(this.draftPortfolioVatConfirm) {
      this.draftPortfolioVatConfirm.preloader = false;
    }
    this.loggerService.error(error);

    if (instrumentsNotInAllowedList(error) || notAllowedStrategies(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.instrumentsNotAllowedForDraftAccept',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else if (isDraftExpiredError(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.draftExpired',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.toastService.unknownApiError();
    }

    this.close();
  }

  private handleReadEnglish(): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.etfLanguageUpdated',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.signalsService.updateEtf();
    this.close();
  }

  public openInitSignIn(): void {
    this.initSignIn = true;
    this.disableClose = false;
    this.preloader = false;
    this.display.next('open');
  }

  public handleInitLogInAddMoney(): void {
    this.close();
    this.router.navigate(['/money/deposit']);
  }

  public handleInitLogInAddPortfolio(): void {
    this.close();
    this.router.navigate(['/portfolio/new']);
  }

  public handleInitLogInAddStrategy(): void {
    this.close();
    this.router.navigate(['/strategy/pick']);
  }

  public handleInitLogInConsultation(): void {
    this.close();
    this.openConsultModal();
  }

  public get efektikaModalChainingDraftData(): ExpectedOwnedStrategy | null {
    return this.acceptEfektikaDraftModalChaning?.draftPortfolio ?? null;
  }

  public openTransactionsExportModal(): void {
    this.transactionsExportForm = true;
    this.display.next('open');
  }

  public openRegularWithdrawConfirmModal(input: RegularWithdrawalDraft): void {
    this.newRegularWithdrawal = { ...input , preloader: false };
    this.display.next('open');
  }

  public confirmNewRegularWithdraw(): void {
    if(!this.newRegularWithdrawal) {
      throw new Error('Attempt to create new regular withdraw without data.');
    }
    this.newRegularWithdrawal.preloader = true;
    if(!this.newRegularWithdrawal.new && this.newRegularWithdrawal.draftId) {
      this.restApiService.acceptWithdrawalDraft({
        regularWithdrawalDraftId: this.newRegularWithdrawal.draftId,
        portfolioId: this.newRegularWithdrawal.formData.portfolioId,
      }).subscribe({
        next: this.handleNewRegularWithdraw.bind(this),
        error: this.handleNewRegularWithdrawalError.bind(this),
      });
    } else {
      this.restApiService.createRegularWithdrawal(this.newRegularWithdrawal.formData).subscribe({
        next: this.handleNewRegularWithdraw.bind(this),
        error: this.handleNewRegularWithdrawalError.bind(this),
      });
    }
  }

  private handleNewRegularWithdrawalError(): void {
    this.toastService.unknownApiError();
    if(this.newRegularWithdrawal) {
      this.newRegularWithdrawal.preloader = false;
    }
  }

  private handleNewRegularWithdraw(): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.' + (this.newRegularWithdrawal?.new ? 'newRegularWithdraw' : 'dipEmailConfirmation'),
      type: 'success',
      component: ToastNotificationComponent
    });
    this.close();
    const redirectRoute = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(redirectRoute ?? '/money/rent');
    });
  }

  public openRegularWithdrawCancelModal(input: CancelRegularWithdrawal): void {
    this.cancelRegularWithdrawal = { ...input , preloader: false };
    this.display.next('open');
  }

  public confirmRegularWithdrawCancelModal(): void {
    if(this.cancelRegularWithdrawal) {
      this.cancelRegularWithdrawal.preloader = true;
      this.restApiService.cancelRegularWithdrawal(this.cancelRegularWithdrawal?.regularWithdrawalId ?? NaN).subscribe({
        next: this.handleRegularWithdrawCancel.bind(this),
        error: this.handleRegularWithdrawCancelError.bind(this),
      });
    }
  }

  private handleRegularWithdrawCancel(): void {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.regularWithdrawCanceled',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.close();
    const redirectRoute = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(redirectRoute ?? '/money/rent');
    });
  }

  private handleRegularWithdrawCancelError(): void {
    this.toastService.unknownApiError();
    if(this.cancelRegularWithdrawal) {
      this.cancelRegularWithdrawal.preloader = false;
    }
  }
}
